import { useEffect, useState } from "react"
import AppsList from "./AppsList"
import Description from "./Description"
import NoResultsCard from "./NoResultsCard"
import SearchBar from "./SearchBar"
import WelcomeCard from "./WelcomeCard"
import PriceInput from "./PriceInput"
import { event } from "utils/gtag"
import useT from "utils/useT"
import useSessionStore from "stores/session"
import useSubmitStore from "stores/submit"

import useSnackbarStore from "stores/snackbar"
import useDebounce from "utils/useDebounce"
import useQueryPlus from "utils/useQueryPlus"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { submitMutation } from "queries"
import SuccessModal from "./SuccessModal"
import Modal from "components/modal"
import NoSsr from "components/NoSsr"
import useMediaQuery from "utils/useMediaQuery"
import extractIdFromLink from "utils/extractIdFromLink"
import dynamic from "next/dynamic"

const LottieAnimation = dynamic(() => import("./LottieAnimation"), {
  ssr: false,
})

export const Pages = () => {
  const t = useT()
  const isDesktop = useMediaQuery("(min-width: 1200px)")
  const queryClient = useQueryClient()
  const [isModalOpen, setModalOpen] = useState(false)
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn)
  const isAdmin = useSessionStore((state) => state.user.isAdmin)
  const toggleToast = useSnackbarStore((state) => state.toggleToast)
  const toggleSnackbar = useSnackbarStore((state) => state.toggleSnackbar)
  const setDescription = useSubmitStore((state) => state.setDescription)
  const submitApp = useSubmitStore((state) => state.submitApp)
  const setPrice = useSubmitStore((state) => state.setPrice)
  const submittedApp = useSubmitStore((state) => state.submittedApp)
  const Step = useSubmitStore((state) => state.step)
  const setStep = useSubmitStore((state) => state.setStep)
  const search = useSubmitStore((state) => state.search)
  const setSearch = useSubmitStore((state) => state.setSearch)
  const debouncedSearch = useDebounce(search, 500)

  const { submitterPrice, submitterDescription } = useSubmitStore(
    (state) => state,
  )
  const { displayName, photoURL, uid } = useSessionStore((state) => state.user)

  const { data, isLoading, fetchStatus } = useQueryPlus({
    enabled: !!debouncedSearch,
    queryKey: ["search", debouncedSearch],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: () =>
      fetch(
        `https://itunes.apple.com/search?term=${search}&entity=software&limit=20&explicit=yes`,
      ).then((res) => res.json()),
  })
  // fetch old price from AppRaven automatically
  const { data: oldPriceData, isLoading: isLoadingOldPriceData } = useQueryPlus(
    {
      enabled: Step === 1 && !!submittedApp?.trackId,
      queryKey: ["old-price", submittedApp?.trackId],
      refetchOnWindowFocus: false,
      queryFn: () =>
        fetch(`/api/fetchOldPrice?trackId=${submittedApp?.trackId}`).then(
          (res) => res.json(),
        ),
    },
  )

  const results = data?.results
  const loading = (isLoading && fetchStatus !== "idle") || isLoadingOldPriceData

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: () =>
      submitMutation({
        displayName,
        photoURL,
        submittedApp,
        submitterDescription,
        submitterPrice,
        uid,
      }),
    onSettled: async (response) => {
      if (response.isSimilar) {
        toggleSnackbar({
          text: t.submit.isSimilar,
          color: "red",
        })
      }
      if (response.acknowledged) {
        queryClient.fetchQuery({
          queryKey: ["getApps"],
        })
        queryClient.fetchQuery({
          queryKey: ["getGames"],
        })
        queryClient.fetchQuery({
          queryKey: ["getAll"],
        })
        queryClient.fetchQuery({
          queryKey: ["getAll" + "discounted"],
        })
        event({
          action: "submit_app",
          category: "Submitted Apps",
          label: "App Submitted",
          value: `App Name: ${submittedApp?.trackName}, AppId: ${submittedApp?.trackId}`,
        })
        setDescription("")
        setPrice("")
        setSearch("")
        setStep(0)
        submitApp()
        setModalOpen(true)
      } else {
        toggleToast({
          text: t.submit.error,
          color: "red",
        })
        event({
          action: "price_update_failed",
          category: "App Price",
          label: "App Price Update Failed",
          value: submittedApp?.trackName,
        })
      }
    },
  })

  useEffect(() => {
    if (oldPriceData?.oldPrice) {
      setPrice(oldPriceData?.oldPrice?.replace("$", ""))
    } else {
      setPrice("")
    }
  }, [Step, submittedApp, oldPriceData])

  useEffect(() => {
    isAdmin && setDescription(data?.results?.[0]?.description)
    event({
      action: "submit_search_input",
      category: "Search",
      label: "Submit Search Request",
      value: debouncedSearch,
    })
  }, [data])

  switch (Step) {
    case 0:
      return (
        <NoSsr>
          <WelcomeCard />
          <SearchBar Loading={loading} />
          {results?.length > 0 ? (
            <AppsList apps={results} title={t.submit.selectApp} />
          ) : (
            <NoResultsCard />
          )}
          <Modal
            isCenter={isDesktop}
            modalOpen={isModalOpen}
            close={() => setModalOpen(false)}
          >
            <>
              {isModalOpen && <LottieAnimation />}
              <SuccessModal />
            </>
          </Modal>
        </NoSsr>
      )
    case 1:
      return (
        <NoSsr>
          <AppsList
            apps={submittedApp ? [submittedApp] : []}
            title={t.submit.selectedApp}
          />
          <PriceInput loading={isLoadingOldPriceData} />
          <Description />
          <div className="content mb-5">
            <button
              onClick={() => {
                if (submitterDescription.length < 30) {
                  toggleSnackbar({
                    color: "yellow",
                    text: t.submit.descriptionTooShort,
                  })
                  return
                }
                mutate()
              }}
              className="btn btn-xl btn-full w-100 mb-3 rounded-m text-uppercase font-600 shadow-s bg-blue-dark"
              disabled={
                !isLoggedIn ||
                !submitterDescription ||
                isSubmitting ||
                isLoadingOldPriceData
              }
            >
              {isSubmitting ? (
                <>
                  <i className="fa fa-cog fa-spin me-2"></i>
                  <span>{t.submit.submittingApp}</span>
                </>
              ) : (
                t.submit.submit
              )}
            </button>
            {!isLoggedIn && (
              <div
                className="d-flex mb-4 alert alert-small rounded-m shadow-xl bg-red-dark"
                role="alert"
              >
                <span className="rounded-start">
                  <i className="fa fa-times text-white"></i>
                </span>
                <strong>{t.submit.loginError}</strong>
              </div>
            )}
          </div>
        </NoSsr>
      )
    default:
      return null
  }
}
export default Pages
